'use client';

import type { MfaMessageDto } from '@terra/shared-contracts-applications';
import { createContext, useContext, type ReactNode } from 'react';
import type { FetchCredentialInboxFn } from '../application-credentials/CredentialInboxMessagesPanel';
import type { RoleFormModalProps } from '../application-roles/RoleFormModal';

export type FetchMfaMessagesFn = (params: {
  credentialId: string;
  mfaConfigId: string;
}) => Promise<{
  success: boolean;
  messages?: MfaMessageDto[];
  otpCode?: string;
  error?: string;
}>;

export type MfaWebhookConfigDetails = {
  endpointPath: string;
  signatureHeader: string;
  timestampHeader: string;
  deliveryIdHeader: string;
  secret: string;
};

export type FetchMfaWebhookConfigResult =
  | ({ success: true } & MfaWebhookConfigDetails)
  | { success: false; error?: string };

export type FetchMfaWebhookConfigFn = (params: {
  credentialId: string;
  mfaConfigId: string;
}) => Promise<FetchMfaWebhookConfigResult>;

export type GenerateCredentialMailboxFn = () => Promise<{
  success: boolean;
  emailAddress?: string;
  error?: string;
}>;

export type CreateCredentialRoleFn = (
  role: Parameters<RoleFormModalProps['onSave']>[0],
) => Promise<{ id: string; name: string } | null>;

export type CredentialDataSources = {
  fetchMfaMessages?: FetchMfaMessagesFn;
  fetchMfaWebhookConfig?: FetchMfaWebhookConfigFn;
  fetchCredentialInbox?: FetchCredentialInboxFn;
  generateMailbox?: GenerateCredentialMailboxFn;
  createRole?: CreateCredentialRoleFn;
};

const CredentialDataSourcesContext = createContext<CredentialDataSources>({});

export const CredentialDataSourcesProvider = ({
  dataSources,
  children,
}: {
  dataSources: CredentialDataSources;
  children: ReactNode;
}) => (
  <CredentialDataSourcesContext.Provider value={dataSources}>
    {children}
  </CredentialDataSourcesContext.Provider>
);

export const useCredentialDataSources = (): CredentialDataSources =>
  useContext(CredentialDataSourcesContext);
